import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import { useFamilyContext } from '../../../contexts/FamilyContext';
import {
  Home,
  User,
  FileText,
  GraduationCap,
  Calendar,
  Brain,
  Eye,
  Settings,
  Users,
  TrendingUp,
  MessageCircle,
  Award,
  Target,
  Shield
} from 'lucide-react';

// Import contexts
import { ParentPortalProvider } from './contexts/ParentPortalContext';
import { ParentAssistanceProvider } from './contexts/ParentAssistanceContext';

// Import sections
import OverviewSection from './sections/OverviewSection';
import SharedDocumentsSection from './sections/SharedDocumentsSection';
import CollegeManagementSection from './sections/CollegeManagementSection';
import TimelineSection from './sections/TimelineSection';
import AssessmentSection from './sections/AssessmentSection';
import InsightsSection from './sections/InsightsSection';
import AccessControlSection from './sections/AccessControlSection';

// Import shared components
import StudentSelector from './components/StudentSelector';
import ProgressIndicator from './components/ProgressIndicator';
import SmartNotifications from './components/SmartNotifications';

const UnifiedParentPortal = () => {
  const { currentUser, familyUsers } = useFamilyContext();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentJourneyPhase, setStudentJourneyPhase] = useState('discovery'); // discovery, planning, execution
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Get current section from URL
  const currentSection = location.pathname.split('/').pop() || 'overview';

  // Get available students (children)
  const students = familyUsers?.filter(user => user.type !== 'parent') || [];

  // Define navigation tabs based on student journey phase
  const getNavigationTabs = (phase) => {
    const baseTabs = [
      {
        id: 'overview',
        label: 'Overview',
        icon: Home,
        description: 'Progress dashboard',
        available: true
      },
      {
        id: 'shared',
        label: 'Shared Documents',
        icon: FileText,
        description: 'Essays, brag sheets, feedback',
        available: true,
        badge: 'newFeedback' // Could show notification badge
      }
    ];

    const phaseSpecificTabs = {
      discovery: [
        {
          id: 'assessment',
          label: 'Strategic Assessment',
          icon: Brain,
          description: 'Parent observations',
          available: true
        }
      ],
      planning: [
        {
          id: 'colleges',
          label: 'College Strategy',
          icon: GraduationCap,
          description: 'Suggestions and planning',
          available: true
        },
        {
          id: 'timeline',
          label: 'Timeline',
          icon: Calendar,
          description: 'Milestones and deadlines',
          available: true
        },
        {
          id: 'assessment',
          label: 'Strategic Input',
          icon: Brain,
          description: 'Parent observations',
          available: true
        }
      ],
      execution: [
        {
          id: 'timeline',
          label: 'Timeline',
          icon: Calendar,
          description: 'Deadlines and progress',
          available: true,
          priority: 'high'
        },
        {
          id: 'colleges',
          label: 'Applications',
          icon: Target,
          description: 'Application status',
          available: true
        },
        {
          id: 'insights',
          label: 'Insights',
          icon: TrendingUp,
          description: 'Strategic analysis',
          available: true
        }
      ]
    };

    const advancedTabs = [
      {
        id: 'insights',
        label: 'Insights',
        icon: TrendingUp,
        description: 'AI-powered analysis',
        available: phase !== 'discovery'
      },
      {
        id: 'access',
        label: 'Permissions',
        icon: Shield,
        description: 'Module access control',
        available: true,
        admin: true
      }
    ];

    return [
      ...baseTabs,
      ...(phaseSpecificTabs[phase] || []),
      ...advancedTabs.filter(tab => tab.available)
    ];
  };

  const navigationTabs = getNavigationTabs(studentJourneyPhase);

  // Auto-select first student if none selected
  useEffect(() => {
    if (students.length > 0 && !selectedStudent) {
      const savedStudentId = localStorage.getItem('parent-portal-selected-student');
      const savedStudent = students.find(s => s.id === savedStudentId);
      setSelectedStudent(savedStudent || students[0]);
    }
  }, [students, selectedStudent]);

  // Update journey phase based on student progress
  useEffect(() => {
    if (selectedStudent) {
      // This would be calculated based on actual student progress
      // For now, we'll use discovery as default
      setStudentJourneyPhase('planning'); // TODO: Calculate based on actual data
    }
  }, [selectedStudent]);

  // Handle navigation
  const handleNavigation = (sectionId) => {
    navigate(`/college/parent/${sectionId}`);
    setShowMobileMenu(false);
  };

  // Get current tab info
  const currentTab = navigationTabs.find(tab => tab.id === currentSection);

  return (
    <ParentPortalProvider value={{ selectedStudent, setSelectedStudent, studentJourneyPhase }}>
      <ParentAssistanceProvider>
        <div className="min-h-screen bg-stone-50">
          {/* Header */}
          <div className="bg-white border-b border-slate-200 sticky top-0 z-40">
            <div className="max-w-7xl mx-auto px-6 py-4">
              <div className="flex items-center justify-between">
                {/* Left side - Portal branding and current section */}
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-azure-500 to-indigo-600 rounded-lg flex items-center justify-center">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h1 className="text-xl font-bold text-slate-900">Parent Portal</h1>
                      {currentTab && (
                        <p className="text-sm text-slate-600">{currentTab.description}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Center - Student selector on desktop */}
                <div className="hidden lg:block">
                  <StudentSelector
                    students={students}
                    selectedStudent={selectedStudent}
                    onStudentChange={setSelectedStudent}
                  />
                </div>

                {/* Right side - User info and notifications */}
                <div className="flex items-center gap-4">
                  <SmartNotifications />
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-slate-600">
                      <strong>{currentUser?.name || 'Parent'}</strong>
                    </span>
                    <div className="w-8 h-8 bg-gradient-to-br from-violet-100 to-violet-200 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-violet-700" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile student selector */}
              <div className="lg:hidden mt-4">
                <StudentSelector
                  students={students}
                  selectedStudent={selectedStudent}
                  onStudentChange={setSelectedStudent}
                  mobile
                />
              </div>
            </div>
          </div>

          {/* Progress indicator */}
          {selectedStudent && (
            <ProgressIndicator
              student={selectedStudent}
              phase={studentJourneyPhase}
            />
          )}

          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="flex gap-8">
              {/* Navigation Sidebar */}
              <div className="hidden lg:block w-64 flex-shrink-0">
                <div className="bg-white rounded-xl border border-slate-200 p-6 sticky top-32">
                  <nav className="space-y-2">
                    {navigationTabs.map((tab) => {
                      const isActive = currentSection === tab.id;
                      const Icon = tab.icon;

                      return (
                        <button
                          key={tab.id}
                          onClick={() => handleNavigation(tab.id)}
                          className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all duration-200 ${
                            isActive
                              ? 'bg-azure-50 border-2 border-azure-500 text-azure-900'
                              : 'hover:bg-slate-50 text-slate-700 border-2 border-transparent'
                          } ${tab.priority === 'high' ? 'ring-2 ring-coral-200' : ''}`}
                        >
                          <Icon
                            size={20}
                            className={isActive ? 'text-azure-600' : 'text-slate-500'}
                          />
                          <div className="flex-1">
                            <div className="font-medium">{tab.label}</div>
                            <div className="text-xs text-slate-500">{tab.description}</div>
                          </div>
                          {tab.badge && (
                            <div className="w-2 h-2 bg-magenta-500 rounded-full"></div>
                          )}
                        </button>
                      );
                    })}
                  </nav>
                </div>
              </div>

              {/* Main Content */}
              <div className="flex-1 min-w-0">
                <Routes>
                  <Route path="/" element={<Navigate to="/college/parent/overview" replace />} />
                  <Route path="/overview" element={<OverviewSection />} />
                  <Route path="/shared" element={<SharedDocumentsSection />} />
                  <Route path="/colleges" element={<CollegeManagementSection />} />
                  <Route path="/timeline" element={<TimelineSection />} />
                  <Route path="/assessment" element={<AssessmentSection />} />
                  <Route path="/insights" element={<InsightsSection />} />
                  <Route path="/access" element={<AccessControlSection />} />
                </Routes>
              </div>
            </div>
          </div>

          {/* Mobile Navigation */}
          {showMobileMenu && (
            <div className="lg:hidden fixed inset-0 bg-black/50 z-50">
              <div className="bg-white w-80 h-full p-6">
                <nav className="space-y-2">
                  {navigationTabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = currentSection === tab.id;

                    return (
                      <button
                        key={tab.id}
                        onClick={() => handleNavigation(tab.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left ${
                          isActive
                            ? 'bg-azure-50 text-azure-900'
                            : 'hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <Icon size={20} />
                        <span className="font-medium">{tab.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>
          )}
        </div>
      </ParentAssistanceProvider>
    </ParentPortalProvider>
  );
};

export default UnifiedParentPortal;